import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Use local API (same server) for focus group management

function FocusGroupAdmin({ user }) {
  const [classCodes, setClassCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());

  // Mission definitions (matches iOS FocusGroupConfig)
  const missionTitles = {
    1: "Make Fathom Your Own",
    2: "The Deep Dive",
    3: "Challenge Mode",
    4: "Pattern Seeker",
    5: "Voice It Out",
    6: "The Mirror"
  };

  function getEmptyFormData() {
    return {
      code: '',
      className: '',
      instructorName: '',
      institution: 'UNK',
      startDate: '',
      endDate: '',
      requiredSessions: 3,
      includeMissions: true,
      surveys: ['evaluation'],
      weeklyAvatarLimit: 10,
      maxDevices: 3,
      accentColor: '#003F72',
      welcomeMessage: '',
      contactEmail: '',
      // Mission visibility (array of mission numbers 1-6, or null for all)
      visibleMissions: null, // null = all missions visible when includeMissions is true
    };
  }

  useEffect(() => {
    fetchClassCodes();
  }, []);

  const fetchClassCodes = async () => {
    try {
      const response = await axios.get('/api/focus-group/class-codes', {
        withCredentials: true,
      });
      setClassCodes(response.data.classCodes || []);
    } catch (error) {
      console.error('Failed to fetch class codes:', error);
      setMessage({ type: 'error', text: 'Failed to load class codes. Make sure you\'re logged in.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        surveys: formData.surveys.filter(s => s),
      };

      await axios.post('/api/focus-group/class-codes', payload, {
        withCredentials: true,
      });

      setMessage({ type: 'success', text: `Class code '${payload.code}' created successfully!` });
      setShowCreateModal(false);
      setFormData(getEmptyFormData());
      fetchClassCodes();
    } catch (error) {
      console.error('Failed to create class code:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create class code';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        ...formData,
        code: editingCode,
        surveys: formData.surveys.filter(s => s),
      };

      await axios.patch('/api/focus-group/class-codes', payload, {
        withCredentials: true,
      });

      setMessage({ type: 'success', text: `Class code '${editingCode}' updated successfully!` });
      setEditingCode(null);
      setFormData(getEmptyFormData());
      fetchClassCodes();
    } catch (error) {
      console.error('Failed to update class code:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update class code';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (code) => {
    if (!window.confirm(`Are you sure you want to deactivate '${code}'? Group members will no longer be able to use this code.`)) {
      return;
    }

    try {
      await axios.delete(`/api/focus-group/class-codes?code=${code}`, {
        withCredentials: true,
      });

      setMessage({ type: 'success', text: `Class code '${code}' deactivated.` });
      fetchClassCodes();
    } catch (error) {
      console.error('Failed to deactivate class code:', error);
      setMessage({ type: 'error', text: 'Failed to deactivate class code' });
    }
  };

  const openEditModal = (classCode) => {
    setEditingCode(classCode.code);
    setFormData({
      code: classCode.code,
      className: classCode.className || '',
      instructorName: classCode.instructorName || '',
      institution: classCode.institution || 'UNK',
      startDate: classCode.startDate?.split('T')[0] || '',
      endDate: classCode.endDate?.split('T')[0] || '',
      requiredSessions: classCode.requiredSessions || 3,
      includeMissions: classCode.includeMissions ?? true,
      surveys: classCode.surveys || ['evaluation'],
      weeklyAvatarLimit: classCode.weeklyAvatarLimit || 10,
      maxDevices: classCode.maxDevices || 3,
      accentColor: classCode.accentColor || '#003F72',
      welcomeMessage: classCode.welcomeMessage || '',
      contactEmail: classCode.contactEmail || '',
      visibleMissions: classCode.visibleMissions || null,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isActive = (classCode) => {
    if (!classCode.isActive) return false;
    const now = new Date();
    const start = new Date(classCode.startDate);
    const end = new Date(classCode.endDate);
    return now >= start && now <= end;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-forest-800 hover:text-forest-700">
                ← Back to Hub
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">
                Focus Group Management
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              <Link
                to="/focus-groups/feedback"
                className="px-4 py-2 text-amber-600 bg-amber-50 rounded-md hover:bg-amber-100 font-medium"
              >
                Feedback
              </Link>
              <Link
                to="/focus-groups/participants"
                className="px-4 py-2 text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 font-medium"
              >
                View Participants
              </Link>
              <button
                onClick={() => {
                  setFormData(getEmptyFormData());
                  setShowCreateModal(true);
                }}
                className="btn-primary"
              >
                + Create Focus Group
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      {message && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className={`px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-forest-800">{classCodes.length}</div>
            <div className="text-sm text-gray-600">Total Focus Groups</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">
              {classCodes.filter(c => isActive(c)).length}
            </div>
            <div className="text-sm text-gray-600">Currently Active</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-blue-600">
              {classCodes.reduce((sum, c) => sum + parseInt(c.enrolledCount || 0, 10), 0)}
            </div>
            <div className="text-sm text-gray-600">Total Enrollments</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-purple-600">
              {classCodes.reduce((sum, c) => sum + parseInt(c.activeCount || 0, 10), 0)}
            </div>
            <div className="text-sm text-gray-600">Active Members</div>
          </div>
        </div>

        {/* Focus Groups Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Instructor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enrolled
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {classCodes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No focus groups found. Create one to get started!
                  </td>
                </tr>
              ) : (
                classCodes.map((classCode) => (
                  <tr key={classCode.code} className={!classCode.isActive ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                        {classCode.code}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{classCode.className}</div>
                      <div className="text-sm text-gray-500">{classCode.institution}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {classCode.instructorName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div>{formatDate(classCode.startDate)}</div>
                      <div className="text-gray-400">to {formatDate(classCode.endDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium">{classCode.enrolledCount || 0} enrolled</div>
                      <div className="text-xs text-gray-500">{classCode.activeCount || 0} active</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isActive(classCode) ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : !classCode.isActive ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Deactivated
                        </span>
                      ) : new Date() < new Date(classCode.startDate) ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Upcoming
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          Expired
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/focus-groups/participants?classCode=${classCode.code}`}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        Members
                      </Link>
                      <button
                        onClick={() => openEditModal(classCode)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </button>
                      {classCode.isActive && (
                        <button
                          onClick={() => handleDeactivate(classCode.code)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCode) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {editingCode ? `Edit: ${editingCode}` : 'Create New Focus Group'}
              </h2>

              <form onSubmit={editingCode ? handleUpdate : handleCreate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Code (only for create) */}
                  {!editingCode && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Class Code *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="Enter a unique class code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Group members will enter this code to join
                      </p>
                    </div>
                  )}

                  {/* Group Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.className}
                      onChange={(e) => setFormData({ ...formData, className: e.target.value })}
                      placeholder="Enter the focus group name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Instructor Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instructor Name
                    </label>
                    <input
                      type="text"
                      value={formData.instructorName}
                      onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                      placeholder="Instructor name (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Institution */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institution
                    </label>
                    <input
                      type="text"
                      value={formData.institution}
                      onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                      placeholder="Institution name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Study Start Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Study End Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Required Sessions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Required Journal Sessions
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.requiredSessions}
                      onChange={(e) => setFormData({ ...formData, requiredSessions: parseInt(e.target.value) || 3 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Weekly Avatar Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weekly Avatar Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.weeklyAvatarLimit}
                      onChange={(e) => setFormData({ ...formData, weeklyAvatarLimit: parseInt(e.target.value) || 10 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Missions Section */}
                  <div className="md:col-span-2 border-t pt-4 mt-2">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Missions</h4>
                        <p className="text-xs text-gray-500">In-app tasks for participants to complete</p>
                      </div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.includeMissions}
                          onChange={(e) => setFormData({
                            ...formData,
                            includeMissions: e.target.checked,
                            visibleMissions: e.target.checked ? formData.visibleMissions : null
                          })}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Enable Missions</span>
                      </label>
                    </div>

                    {formData.includeMissions && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-2">Select which missions are visible to participants:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[1, 2, 3, 4, 5, 6].map((num) => {
                            const isSelected = !formData.visibleMissions || formData.visibleMissions.includes(num);
                            return (
                              <label key={num} className="flex items-center space-x-2 p-2 bg-white rounded border cursor-pointer hover:bg-indigo-50">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    let newVisible = formData.visibleMissions ? [...formData.visibleMissions] : [1, 2, 3, 4, 5, 6];
                                    if (e.target.checked) {
                                      if (!newVisible.includes(num)) newVisible.push(num);
                                    } else {
                                      newVisible = newVisible.filter(n => n !== num);
                                    }
                                    newVisible.sort((a, b) => a - b);
                                    setFormData({
                                      ...formData,
                                      visibleMissions: newVisible.length === 6 ? null : newVisible
                                    });
                                  }}
                                  className="rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-700">{num}. {missionTitles[num]}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          {!formData.visibleMissions
                            ? 'All missions visible'
                            : `${formData.visibleMissions.length} mission(s) selected`}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Contact Email */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      placeholder="Contact email for participants"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Welcome Message */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Welcome Message (optional)
                    </label>
                    <textarea
                      rows="3"
                      value={formData.welcomeMessage}
                      onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                      placeholder="Custom welcome message for group members..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Accent Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Accent Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300"
                      />
                      <input
                        type="text"
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                        placeholder="#003F72"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingCode(null);
                      setFormData(getEmptyFormData());
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : (editingCode ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FocusGroupAdmin;
